import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'

import ReportFilters from '../../../shared/ReportFilters'
import ReportJobsList from '../../../shared/ReportJobsList'
import useAsyncReport from '../../../hooks/useAsyncReport'
import {
  addInternalPharmacySalesJob,
  updateInternalPharmacySalesJob,
  removeInternalPharmacySalesJob,
} from '../../../redux/slices/AppSlice'
import { getPayersList } from '../../../redux/slices/VisitsSlice'
import { payersOptionsFormat } from '../../../constants/BeneficiaryConstants'
import { validateOneMonthRange, getMinDate, getOneMonthMaxDate } from '../../../utils/dateRangeValidation'

const InternalPharmacySalesReportAsync = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const jobs = useSelector(state => state.app.internalPharmacySalesJobs || [])
  const { payersData, isLoading: isLoadingPayers } = useSelector(state => state.visits)

  const [filters, setFilters] = useState({
    start_date: moment().toDate(),
    end_date: moment().toDate(),
    invoice_number: '',
    file_number: '',
    payer_ids: [],
    format: 'excel',
  })

  useEffect(() => {
    dispatch(getPayersList())
  }, [dispatch])

  const payerOptions = useMemo(() => payersOptionsFormat(payersData?.data), [payersData?.data])

  const { loading, downloadingJobs, startJob, downloadReport, removeJob, retryJob } = useAsyncReport({
    addJobAction: addInternalPharmacySalesJob,
    updateJobAction: updateInternalPharmacySalesJob,
    removeJobAction: removeInternalPharmacySalesJob,
    createEndpoint: '/v3/reports/internal-pharmacy-sales/jobs',
    statusEndpoint: '/v3/reports/internal-pharmacy-sales/jobs',
    downloadEndpoint: '/v3/reports/internal-pharmacy-sales/jobs',
    fileNamePrefix: 'internal_pharmacy_sales',
    existingJobs: jobs,
    buildPayload: (f, format) => ({
      start_date: moment(f.start_date).format('YYYY-MM-DD'),
      end_date: moment(f.end_date).format('YYYY-MM-DD'),
      format,
      ...(f.invoice_number && { invoice_number: f.invoice_number }),
      ...(f.file_number && { file_number: f.file_number }),
      ...(f.payer_ids?.length && { payer_ids: f.payer_ids }),
    }),
  })

  const handleGenerate = () => {
    if (!validateOneMonthRange(filters.start_date, filters.end_date, t)) return
    startJob(filters, filters.format)
  }

  const handleReset = () =>
    setFilters({
      start_date: moment().toDate(),
      end_date: moment().toDate(),
      invoice_number: '',
      file_number: '',
      payer_ids: [],
      format: 'excel',
    })

  const handleChange = e => setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }))

  const filterConfig = useMemo(() => [
    { type: 'date',   name: 'start_date',     label: 'Start Date', width: 'w-[160px]' },
    { type: 'date',   name: 'end_date',       label: 'End Date',   width: 'w-[160px]', minDate: getMinDate(filters.start_date), maxDate: getOneMonthMaxDate(filters.start_date) },
    { type: 'text',   name: 'invoice_number', label: 'Invoice No', placeholder: 'Enter Invoice No', width: 'w-[160px]' },
    { type: 'text',   name: 'file_number',    label: 'MRN',    placeholder: 'Enter MRN',    width: 'w-[160px]' },
    {
      type: 'multiselect',
      name: 'payer_ids',
      label: 'Payer',
      placeholder: 'Select Payer',
      options: payerOptions,
      optionGroupLabel: 'label',
      optionGroupChildren: 'options',
      filter: true,
      display: 'chip',
      maxSelectedLabels: 2,
      selectedItemsLabel: '{0} payers selected',
      disabled: isLoadingPayers,
      width: 'w-[280px]',
    },
    {
      type: 'dropdown',
      name: 'format',
      label: 'Format',
      options: [
        { label: 'Excel', value: 'excel' },
        { label: 'PDF',   value: 'pdf'   },
      ],
      width: 'w-[130px]',
    },
  ], [filters.start_date, isLoadingPayers, payerOptions])

  return (
    <div>
      <ReportFilters
        filters={filters}
        onChange={handleChange}
        filterConfig={filterConfig}
        onGenerate={handleGenerate}
        onReset={handleReset}
        loading={loading}
      />
      <ReportJobsList
        jobs={jobs}
        onDownload={downloadReport}
        onRemove={removeJob}
        onRetry={retryJob}
        downloadingJobs={downloadingJobs}
        reportTitle={t('Internal Pharmacy Sales')}
      />
    </div>
  )
}

export default InternalPharmacySalesReportAsync
